import React, { useMemo } from 'react';

import { connect } from 'react-redux';

import { List, ListItemButton, ListItemText, ListSubheader } from '@mui/material';

import { useGetTourServices } from '~/hooks/apis/tours/useTourCostPrices';

import { customerPortalHost } from '~/services/common';

import { isNativeTourModelEnabled } from '../nativeTourModel';

import TourSupplierVendorLink from './TourSupplierVendorLink';

interface PreviewLink {
  href: string;
  label: string;
}

interface MappedStateProps {
  tenant?: App.Tenant;
}

interface Props {
  tourId: string;
  tourTitle: string;
  vendorId?: string;
}

function TourDetailsLinksList(props: Props & MappedStateProps) {
  const { tenant, tourId, tourTitle, vendorId } = props;

  // Where the native model is off (production), this only reads what the pricing tab has
  // already loaded rather than asking for services every tour page.
  const { data: services = [] } = useGetTourServices(tourId, { enabled: isNativeTourModelEnabled() });
  const supplierServices = useMemo(
    () =>
      services.filter(
        (service, index) =>
          !!service.sourceSupplierId &&
          services.findIndex((other) => other.sourceSupplierId === service.sourceSupplierId) === index,
      ),
    [services],
  );

  const customerPreviewLinks = useMemo<Array<PreviewLink>>(() => {
    const links: Array<PreviewLink> = [];

    if (tourId) {
      links.push({
        href: `${customerPortalHost(tenant)}/tour/tour-${tourId}`,
        label: tenant.title,
      });
    }

    return links;
  }, [tenant, tourId]);

  const vendorPreviewLinks = useMemo<Array<PreviewLink>>(() => {
    const links: Array<PreviewLink> = [];

    if (vendorId) {
      links.push({
        href: `/vendors/${vendorId}`,
        label: vendorId,
      });
    }

    return links;
  }, [vendorId]);

  return (
    <List>
      <ListSubheader disableSticky sx={{ lineHeight: 1.4 }}>
        {tourTitle}
      </ListSubheader>
      {customerPreviewLinks?.length > 0 && (
        <>
          <ListSubheader disableSticky color="primary">
            Preview on:
          </ListSubheader>
          {customerPreviewLinks.map((link) => (
            <ListItemButton key={link.href} href={link.href} target="_blank" rel="noreferrer">
              <ListItemText primary={link.label} />
            </ListItemButton>
          ))}
        </>
      )}

      {/* The tour's vendorId is the umbrella account; its contracted DMCs are the real suppliers. */}
      {supplierServices.length > 0 && (
        <>
          <ListSubheader disableSticky color="primary">
            Suppliers:
          </ListSubheader>
          {supplierServices.map((service) => (
            <TourSupplierVendorLink
              key={service.sourceSupplierId}
              supplierId={service.supplierId}
              sourceSupplierId={service.sourceSupplierId!}
            />
          ))}
        </>
      )}

      {!supplierServices.length && vendorPreviewLinks?.length > 0 && (
        <>
          <ListSubheader disableSticky color="primary">
            Vendor:
          </ListSubheader>
          {vendorPreviewLinks.map((link) => (
            <ListItemButton key={link.href} href={link.href} target="_blank" rel="noreferrer">
              <ListItemText primary={link.label} />
            </ListItemButton>
          ))}
        </>
      )}
    </List>
  );
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => ({
  tenant: state.tenant,
}))(TourDetailsLinksList);
